import React, { useEffect, useRef, useState } from 'react';
import {useNavigate, useLocation } from 'react-router-dom';
import * as THREE from 'three';
import { useAuth } from '../contexts/AuthContext';
import '../styles/LandingPage.css';

const LandingPage = () => {
  const { user, login, register, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const [uiState, setUiState] = useState('landing'); // 'landing' | 'auth'
  const [authType, setAuthType] = useState('login'); // 'login' | 'signup'
  
  // Login Form State
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Register Form State
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Three.js animation refs
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const cityGroupRef = useRef(null);
  const requestRef = useRef(null);
  
  // Animation targets
  const targetCameraX = useRef(0);
  const targetCameraZ = useRef(30);
  const xOffset = useRef(0);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/overview';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  // Handle location state for opening auth
  useEffect(() => {
    if (location.state?.openAuth) {
        setUiState('auth');
        setAuthType(location.state.openAuth); // 'login' or 'signup'
    }
  }, [location.state]);

  // Clear errors when switching auth type
  useEffect(() => {
    if (error) clearError();
    setFormErrors({});
  }, [authType, clearError]);

  // Three.js Initialization
  useEffect(() => {
    if (!canvasRef.current) return;

    // Scene config
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#f4f4f5'); // Matches CSS body bg
    scene.fog = new THREE.Fog('#f4f4f5', 10, 50);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 15, 30);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    canvasRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // City Generation
    const cityGroup = new THREE.Group();
    scene.add(cityGroup);
    cityGroupRef.current = cityGroup;

    // Materials
    const buildingMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xcccccc, wireframe: true, transparent: true, opacity: 0.3 });
    const accentMat = new THREE.MeshBasicMaterial({ color: 0x2563eb });

    // Shared Geometries
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    geometry.translate(0, 0.5, 0);
    const topGeo = new THREE.PlaneGeometry(1, 1);
    topGeo.rotateX(-Math.PI / 2);

    const gridSize = 6;
    const spacing = 2.5;
    const maxCount = (gridSize * 2 + 1) ** 2;

    const mesh = new THREE.InstancedMesh(geometry, buildingMat, maxCount);
    const lineMesh = new THREE.InstancedMesh(geometry, lineMat, maxCount);
    const accentMesh = new THREE.InstancedMesh(topGeo, accentMat, maxCount); // For blue tops

    mesh.castShadow = true;
    mesh.receiveShadow = true;

    const dummy = new THREE.Object3D();
    let count = 0;
    let accentCount = 0;
    
    for(let x = -gridSize; x <= gridSize; x++) {
        for(let z = -gridSize; z <= gridSize; z++) {
            // Chance to spawn a building
            if(Math.random() > 0.4) {
                const height = Math.random() * 6 + 1;
                
                dummy.position.set(x * spacing, 0, z * spacing);
                dummy.scale.set(1.5, height, 1.5);
                dummy.updateMatrix();
                
                mesh.setMatrixAt(count, dummy.matrix);
                lineMesh.setMatrixAt(count, dummy.matrix);
                
                // Occasional "active" building (blue top)
                if (Math.random() > 0.95) {
                    // Reset scale for top
                    dummy.scale.set(1.5, 1, 1.5);
                    dummy.position.y = height + 0.01;
                    dummy.updateMatrix();
                    accentMesh.setMatrixAt(accentCount++, dummy.matrix);
                }
                
                count++;
            }
        }
    }
    
    mesh.count = count;
    lineMesh.count = count;
    accentMesh.count = accentCount;

    mesh.instanceMatrix.needsUpdate = true;
    lineMesh.instanceMatrix.needsUpdate = true;
    accentMesh.instanceMatrix.needsUpdate = true;

    cityGroup.add(mesh);
    cityGroup.add(lineMesh);
    cityGroup.add(accentMesh);

    // Floor plane
    const planeGeometry = new THREE.PlaneGeometry(100, 100);
    const planeMaterial = new THREE.MeshLambertMaterial({ color: 0xf4f4f5 });
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.rotation.x = -Math.PI / 2;
    plane.receiveShadow = true;
    scene.add(plane);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight.position.set(20, 40, 20);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 1024;
    dirLight.shadow.mapSize.height = 1024;
    scene.add(dirLight);

    // Animation Loop
    const animate = () => {
        requestRef.current = requestAnimationFrame(animate);

        if (cityGroupRef.current) {
            cityGroupRef.current.rotation.y += 0.001;
        }

        if (cameraRef.current) {
            cameraRef.current.position.x += (targetCameraX.current + xOffset.current - cameraRef.current.position.x) * 0.05;
            cameraRef.current.position.z += (targetCameraZ.current - cameraRef.current.position.z) * 0.05;
            cameraRef.current.lookAt(0, 5, 0);
        }

        if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
    };
    animate();

    // Resize Handler
    const handleResize = () => {
        if (cameraRef.current && rendererRef.current) {
            cameraRef.current.aspect = window.innerWidth / window.innerHeight;
            cameraRef.current.updateProjectionMatrix();
            rendererRef.current.setSize(window.innerWidth, window.innerHeight);
        }
    };
    window.addEventListener('resize', handleResize);

    // Mouse Move Handler
    const handleMouseMove = (e) => {
        if (uiState === 'landing') {
            const mouseX = (e.clientX / window.innerWidth) * 2 - 1;
            targetCameraX.current = mouseX * 2;
        }
    };
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('mousemove', handleMouseMove);
        if (requestRef.current) {
            cancelAnimationFrame(requestRef.current);
        }
        
        // Proper cleanup
        if (rendererRef.current) {
            rendererRef.current.dispose();
            if (rendererRef.current.forceContextLoss) rendererRef.current.forceContextLoss();
            
            if (canvasRef.current && rendererRef.current.domElement) {
                canvasRef.current.removeChild(rendererRef.current.domElement);
            }
        }

        // Dispose geometries and materials
        geometry.dispose();
        topGeo.dispose();
        planeGeometry.dispose();
        
        buildingMat.dispose();
        lineMat.dispose();
        accentMat.dispose();
        planeMaterial.dispose();
        
        // Clear scene
        while(scene.children.length > 0){ 
            scene.remove(scene.children[0]); 
        }
    };
  }, []); // Run once on mount

  // Watch for UI state changes to update camera
  useEffect(() => {
    if (uiState === 'auth') {
        xOffset.current = -10;
    } else {
        xOffset.current = 0;
        targetCameraX.current = 0;
    }
  }, [uiState]);

  // Form Handlers
  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    if (formErrors[e.target.name]) {
        setFormErrors({ ...formErrors, [e.target.name]: '' });
    }
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({ ...prev, [name]: value }));
    
    // Clear field-specific errors when user starts typing
    if (formErrors[name]) {
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear global error when user starts typing
    if (error) {
        clearError();
    }
  };

  const validateLogin = () => {
    const errors = {};
    if (!loginData.email) errors.email = 'Email is required';
    if (!loginData.password) errors.password = 'Password is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRegister = () => {
    const errors = {};
    
    // First Name validation
    if (!registerData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (registerData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    } else if (registerData.firstName.trim().length > 50) {
      errors.firstName = 'First name must be less than 50 characters';
    }

    // Last Name validation
    if (!registerData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (registerData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    } else if (registerData.lastName.trim().length > 50) {
      errors.lastName = 'Last name must be less than 50 characters';
    }

    // Email validation
    if (!registerData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(registerData.email)) {
      errors.email = 'Email is invalid';
    }

    // Password validation
    if (!registerData.password) {
      errors.password = 'Password is required';
    } else if (registerData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(registerData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Confirm Password validation
    if (!registerData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (registerData.password !== registerData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;

    try {
        await login(loginData.email, loginData.password);
        // Navigation handled by useEffect when user state changes
    } catch (err) {
        console.error("Login failed", err);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!validateRegister()) return;

    try {
        await register({
            firstName: registerData.firstName.trim(),
            lastName: registerData.lastName.trim(),
            email: registerData.email.toLowerCase().trim(),
            password: registerData.password
        });
        // Navigation handled by useEffect when user state changes
    } catch (err) {
        console.error("Registration failed", err);
    }
  };

  const showAuth = (type) => {
    setUiState('auth');
    setAuthType(type);
    if (error) clearError();
    setFormErrors({});
  };

  const closeAuth = () => {
    setUiState('landing');
    if (error) clearError();
    setFormErrors({});
  };

  return (
    <div className="landing-page-wrapper">
      {/* 3D Background */}
      <div id="canvas-container" ref={canvasRef}></div>

      {/* UI Overlay */}
      <div id="ui-layer">
        <nav className="landing-nav">
          <div className="landing-logo">
            <div className="logo-square"></div>
            Realista
          </div>
          <div className="landing-nav-links">
            <button onClick={() => showAuth('login')}>Log In</button>
            {/* <button className="landing-btn-primary" onClick={() => showAuth('signup')}>Get Started</button> */} {/* Removed until production */}
          </div>
        </nav>

        <div className="landing-content-wrapper">
            
          {/* Hero Content */}
          <div className={`hero-section ${uiState === 'auth' ? 'hidden-left' : ''}`}>
            <h1>The Future of<br/>Real Estate Intelligence.</h1>
            <p>
              Leverage our AI-driven agent to analyze market trends, predict property values, and optimize your portfolio. 
              Minimal data input, maximum insight.
            </p>
            <button className="landing-btn-primary" onClick={() => showAuth('signup')}>Start Analysis</button>
          </div>

          {/* Auth Container (Login/Signup) */}
          <div className={`auth-section ${uiState === 'auth' ? 'active' : ''}`}>
            
            {/* Global Error Message */}
            {error && <div className="alert alert-error">{error}</div>}

            {/* Login Form */}
            {authType === 'login' && (
              <div>
                <div className="auth-header">
                  <h2>Welcome Back</h2>
                  <p>Enter your credentials</p>
                </div>
                <form onSubmit={handleLoginSubmit}>
                  <div className="landing-form-group">
                    <label className="landing-label">Email Address</label>
                    <input 
                      type="email" 
                      name="email"
                      className="landing-input" 
                      placeholder="name@company.com" 
                      value={loginData.email}
                      onChange={handleLoginChange}
                      disabled={loading}
                    />
                    {formErrors.email && <p className="error-message">{formErrors.email}</p>}
                  </div>
                  <div className="landing-form-group">
                    <label className="landing-label">Password</label>
                    <input 
                      type="password" 
                      name="password"
                      className="landing-input" 
                      placeholder="••••••••" 
                      value={loginData.password}
                      onChange={handleLoginChange}
                      disabled={loading}
                    />
                    {formErrors.password && <p className="error-message">{formErrors.password}</p>}
                  </div>
                  <button type="submit" className="landing-submit-btn" disabled={loading}>
                    {loading ? <div className="loading-spinner-small"></div> : null}
                    {loading ? 'Signing In...' : 'Sign In'}
                  </button>
                </form>
                {/* <div className="switch-auth">
                  Don't have an account? <span onClick={() => showAuth('signup')}>Sign Up</span> ///Commented out until production///
                </div> */}
                <div className="switch-auth">
                  <span onClick={closeAuth} style={{color: 'var(--landing-text-muted)', fontWeight: 400}}>← Back to Home</span>
                </div>
              </div>
            )}

            {/* Signup Form */}
            {authType === 'signup' && (
              <div>
                <div className="auth-header">
                  <h2>Create Account</h2>
                  <p>Join Realista to start intelligence hunting today.</p>
                </div>
                <form onSubmit={handleRegisterSubmit}>
                  <div className="landing-form-group">
                    <label className="landing-label">First Name</label>
                    <input 
                      type="text" 
                      name="firstName"
                      className="landing-input" 
                      placeholder="John" 
                      value={registerData.firstName}
                      onChange={handleRegisterChange}
                      disabled={loading}
                      autoComplete="given-name"
                    />
                    {formErrors.firstName && <p className="error-message">{formErrors.firstName}</p>}
                  </div>
                  <div className="landing-form-group">
                    <label className="landing-label">Last Name</label>
                    <input 
                      type="text" 
                      name="lastName"
                      className="landing-input" 
                      placeholder="Doe" 
                      value={registerData.lastName}
                      onChange={handleRegisterChange}
                      disabled={loading}
                      autoComplete="family-name"
                    />
                    {formErrors.lastName && <p className="error-message">{formErrors.lastName}</p>}
                  </div>
                  <div className="landing-form-group">
                    <label className="landing-label">Email Address</label>
                    <input 
                      type="email" 
                      name="email"
                      className="landing-input" 
                      placeholder="name@company.com" 
                      value={registerData.email}
                      onChange={handleRegisterChange}
                      disabled={loading}
                      autoComplete="email"
                    />
                    {formErrors.email && <p className="error-message">{formErrors.email}</p>}
                  </div>
                  <div className="landing-form-group">
                    <label className="landing-label">Password</label>
                    <input 
                      type="password" 
                      name="password"
                      className="landing-input" 
                      placeholder="••••••••" 
                      value={registerData.password}
                      onChange={handleRegisterChange}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    {formErrors.password && <p className="error-message">{formErrors.password}</p>}
                  </div>
                  <div className="landing-form-group">
                    <label className="landing-label">Confirm Password</label>
                    <input 
                      type="password" 
                      name="confirmPassword"
                      className="landing-input" 
                      placeholder="••••••••" 
                      value={registerData.confirmPassword}
                      onChange={handleRegisterChange}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    {formErrors.confirmPassword && <p className="error-message">{formErrors.confirmPassword}</p>}
                  </div>
                  <button type="submit" className="landing-submit-btn" disabled={loading}>
                    {loading ? <div className="loading-spinner-small"></div> : null}
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>
                <div className="switch-auth">
                  Already have an account? <span onClick={() => showAuth('login')}>Log In</span>
                </div>
                <div className="switch-auth">
                  <span onClick={closeAuth} style={{color: 'var(--landing-text-muted)', fontWeight: 400}}>← Back to Home</span>
                </div>
              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
};

export default LandingPage;
